<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import ReportsAPI from 'dashboard/api/reports';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import Spinner from 'shared/components/Spinner.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  ceoData: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const PERIODS = {
  LAST_7_DAYS: { days: 7, key: 'LAST_7_DAYS' },
  LAST_30_DAYS: { days: 30, key: 'LAST_30_DAYS' },
  LAST_90_DAYS: { days: 90, key: 'LAST_90_DAYS' },
};

const selectedPeriod = ref(PERIODS.LAST_7_DAYS);
const isLoading = ref(false);
const metrics = ref({
  conversationCount: 0,
  resolutionRate: 0,
  handoffRate: 0,
  avgResponseTime: 0,
});
const previousMetrics = ref({
  conversationCount: 0,
  resolutionRate: 0,
  handoffRate: 0,
  avgResponseTime: 0,
});

const periodMenuList = computed(() =>
  Object.values(PERIODS).map(period => ({
    label: t(`CAPTAIN_SUMMARY.PERIOD.${period.key}`),
    value: period.key,
    action: 'select',
  }))
);

const selectedPeriodLabel = computed(() =>
  t(`CAPTAIN_SUMMARY.PERIOD.${selectedPeriod.value.key}`)
);

const [showDropdown, toggleDropdown] = useToggle();

const getDateRange = period => {
  const now = Math.floor(Date.now() / 1000);
  const from = now - period.days * 86400;
  return { from, to: now };
};

const getPreviousDateRange = period => {
  const now = Math.floor(Date.now() / 1000);
  const from = now - period.days * 2 * 86400;
  const to = now - period.days * 86400;
  return { from, to };
};

const fetchMetrics = async () => {
  isLoading.value = true;
  try {
    const currentRange = getDateRange(selectedPeriod.value);
    const previousRange = getPreviousDateRange(selectedPeriod.value);

    const [
      currentBotResponse,
      previousBotResponse,
      currentSummary,
      previousSummary,
    ] = await Promise.all([
      ReportsAPI.getBotMetrics(currentRange),
      ReportsAPI.getBotMetrics(previousRange),
      ReportsAPI.getSummary(currentRange.from, currentRange.to),
      ReportsAPI.getSummary(previousRange.from, previousRange.to),
    ]);

    const botAutonomyRate = props.ceoData?.bot_autonomy?.bot_rate;
    const ceoAvgResponseTime = props.ceoData?.ai_response_time?.avg_seconds;

    metrics.value = {
      conversationCount: currentBotResponse.data.conversation_count || 0,
      resolutionRate:
        botAutonomyRate != null
          ? botAutonomyRate
          : currentBotResponse.data.resolution_rate || 0,
      handoffRate: currentBotResponse.data.handoff_rate || 0,
      avgResponseTime:
        ceoAvgResponseTime != null
          ? ceoAvgResponseTime
          : currentSummary.data.avg_first_response_time || 0,
    };

    previousMetrics.value = {
      conversationCount: previousBotResponse.data.conversation_count || 0,
      resolutionRate: previousBotResponse.data.resolution_rate || 0,
      handoffRate: previousBotResponse.data.handoff_rate || 0,
      avgResponseTime: previousSummary.data.avg_first_response_time || 0,
    };
  } catch {
    // Silently handle errors -- metrics will show defaults
  } finally {
    isLoading.value = false;
  }
};

const handlePeriodChange = ({ value }) => {
  toggleDropdown(false);
  selectedPeriod.value = PERIODS[value];
  fetchMetrics();
};

const formatPercent = value => {
  if (!value && value !== 0) return '--';
  return `${Math.round(value)}%`;
};

const formatTime = seconds => {
  if (!seconds) return '--';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

const formatCount = value => {
  if (!value && value !== 0) return '--';
  return value.toLocaleString();
};

const calculateTrend = (current, previous) => {
  if (!previous || previous === 0) return { value: 0, direction: 'flat' };
  const change = ((current - previous) / previous) * 100;
  if (Math.abs(change) < 0.5) return { value: 0, direction: 'flat' };
  return {
    value: Math.abs(Math.round(change)),
    direction: change > 0 ? 'up' : 'down',
  };
};

const metricCards = computed(() => {
  const cards = [
    {
      id: 'resolution_rate',
      label: t('CAPTAIN_SUMMARY.RESOLUTION_RATE.LABEL'),
      tooltip: t('CAPTAIN_SUMMARY.RESOLUTION_RATE.TOOLTIP'),
      value: formatPercent(metrics.value.resolutionRate),
      trend: calculateTrend(
        metrics.value.resolutionRate,
        previousMetrics.value.resolutionRate
      ),
      positiveDirection: 'up',
    },
    {
      id: 'ai_conversations',
      label: t('CAPTAIN_SUMMARY.AI_CONVERSATIONS.LABEL'),
      tooltip: t('CAPTAIN_SUMMARY.AI_CONVERSATIONS.TOOLTIP'),
      value: formatCount(metrics.value.conversationCount),
      trend: calculateTrend(
        metrics.value.conversationCount,
        previousMetrics.value.conversationCount
      ),
      positiveDirection: 'up',
    },
    {
      id: 'handoff_rate',
      label: t('CAPTAIN_SUMMARY.HANDOFF_RATE.LABEL'),
      tooltip: t('CAPTAIN_SUMMARY.HANDOFF_RATE.TOOLTIP'),
      value: formatPercent(metrics.value.handoffRate),
      trend: calculateTrend(
        metrics.value.handoffRate,
        previousMetrics.value.handoffRate
      ),
      positiveDirection: 'down',
    },
    {
      id: 'avg_response_time',
      label: t('CAPTAIN_SUMMARY.AVG_RESPONSE_TIME.LABEL'),
      tooltip: t('CAPTAIN_SUMMARY.AVG_RESPONSE_TIME.TOOLTIP'),
      value: formatTime(metrics.value.avgResponseTime),
      trend: calculateTrend(
        metrics.value.avgResponseTime,
        previousMetrics.value.avgResponseTime
      ),
      positiveDirection: 'down',
    },
  ];

  if (props.ceoData) {
    cards.push(
      {
        id: 'total_refunds',
        label: t('CAPTAIN_SUMMARY.TOTAL_REFUNDS.LABEL'),
        tooltip: t('CAPTAIN_SUMMARY.TOTAL_REFUNDS.TOOLTIP'),
        value: formatCount(props.ceoData.total_refunds || 0),
        trend: { value: 0, direction: 'flat' },
        positiveDirection: 'down',
      },
      {
        id: 'total_cancellations',
        label: t('CAPTAIN_SUMMARY.TOTAL_CANCELLATIONS.LABEL'),
        tooltip: t('CAPTAIN_SUMMARY.TOTAL_CANCELLATIONS.TOOLTIP'),
        value: formatCount(props.ceoData.total_cancellations || 0),
        trend: { value: 0, direction: 'flat' },
        positiveDirection: 'down',
      }
    );
  }

  return cards;
});

const trendColor = (trend, positiveDirection) => {
  if (trend.direction === 'flat') return 'text-n-slate-11';
  const isPositive =
    (trend.direction === 'up' && positiveDirection === 'up') ||
    (trend.direction === 'down' && positiveDirection === 'down');
  return isPositive ? 'text-n-teal-11' : 'text-n-ruby-11';
};

const trendBgColor = (trend, positiveDirection) => {
  if (trend.direction === 'flat') return 'bg-n-alpha-2';
  const isPositive =
    (trend.direction === 'up' && positiveDirection === 'up') ||
    (trend.direction === 'down' && positiveDirection === 'down');
  return isPositive ? 'bg-n-teal-3' : 'bg-n-ruby-3';
};

const trendArrow = trend => {
  if (trend.direction === 'up') return '\u2191';
  if (trend.direction === 'down') return '\u2193';
  return '';
};

const { startRefetching } = useLiveRefresh(fetchMetrics, 120000);

onMounted(() => {
  fetchMetrics();
  startRefetching();
});
</script>

<template>
  <div
    class="flex flex-col m-0.5 px-6 py-5 rounded-xl text-n-slate-12 shadow outline-1 outline outline-n-container bg-n-solid-2"
  >
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-2">
        <fluent-icon icon="bot-outline" size="18" class="text-n-blue-11" />
        <h5 class="mb-0 text-n-slate-12 font-medium text-lg">
          {{ t('CAPTAIN_SUMMARY.HEADER') }}
        </h5>
        <span
          class="flex flex-row items-center py-0.5 px-2 rounded bg-n-teal-3 text-xs"
        >
          <span
            class="bg-n-teal-9 h-1 w-1 rounded-full mr-1 rtl:mr-0 rtl:ml-0"
          />
          <span class="text-xs text-n-teal-11">
            {{ $t('OVERVIEW_REPORTS.LIVE') }}
          </span>
        </span>
      </div>
      <div
        v-on-clickaway="() => toggleDropdown(false)"
        class="relative flex items-center group z-50"
      >
        <Button
          sm
          slate
          faded
          :label="selectedPeriodLabel"
          class="capitalize rounded-md group-hover:bg-n-alpha-2"
          @click="toggleDropdown()"
        />
        <DropdownMenu
          v-if="showDropdown"
          :menu-items="periodMenuList"
          class="mt-1 ltr:right-0 rtl:left-0 top-full"
          @action="handlePeriodChange($event)"
        />
      </div>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-6">
      <Spinner />
      <span class="text-n-slate-11 ml-2">
        {{ t('CAPTAIN_SUMMARY.LOADING_MESSAGE') }}
      </span>
    </div>

    <div v-else class="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
      <div
        v-for="card in metricCards"
        :key="card.id"
        class="flex flex-col gap-1 min-w-0"
      >
        <h3 class="flex items-center m-0 text-sm font-medium text-n-slate-11">
          <span>{{ card.label }}</span>
          <fluent-icon
            v-tooltip="card.tooltip"
            size="14"
            icon="info"
            class="text-n-slate-11 my-0 mx-1 mt-0.5"
          />
        </h3>
        <div class="flex items-baseline gap-2">
          <h4 class="mt-1 mb-0 text-2xl font-semibold text-n-slate-12">
            {{ card.value }}
          </h4>
          <span
            v-if="card.trend.direction !== 'flat'"
            :class="[
              trendColor(card.trend, card.positiveDirection),
              trendBgColor(card.trend, card.positiveDirection),
            ]"
            class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
          >
            {{ trendArrow(card.trend) }}{{ card.trend.value }}%
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
